import { createContext, useContext, ReactNode, useState, useEffect } from 'react'

import TrendV2Monitor from './TrendV2Monitor'

import { TREND_V2_TRACKING_KEY, TrendV2TrackingItem } from '@/constants/trend_v2'

interface TrendV2ContextType {
  trackingItems: TrendV2TrackingItem[]
  isPending: (conversationId: string) => boolean
}

const TrendV2Context = createContext<TrendV2ContextType | undefined>(undefined)

export const TrendV2CachingProvider = ({ children }: { children: ReactNode }) => {
  const [trackingItems, setTrackingItems] = useState<TrendV2TrackingItem[]>([])

  useEffect(() => {
    const stored = localStorage.getItem(TREND_V2_TRACKING_KEY)
    if (stored) {
      try {
        const items: TrendV2TrackingItem[] = JSON.parse(stored)
        setTrackingItems(items)
      } catch (error) {
        console.error('Error parsing TrendV2 tracking data:', error)
        localStorage.removeItem(TREND_V2_TRACKING_KEY)
      }
    }

    const checkLocalStorage = () => {
      const stored = localStorage.getItem(TREND_V2_TRACKING_KEY)
      if (stored) {
        const items: TrendV2TrackingItem[] = JSON.parse(stored)
        setTrackingItems(items)
      } else {
        setTrackingItems([])
      }
    }

    const interval = setInterval(checkLocalStorage, 3000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === TREND_V2_TRACKING_KEY) {
        if (e.newValue) {
          try {
            const items: TrendV2TrackingItem[] = JSON.parse(e.newValue)
            setTrackingItems(items)
          } catch (error) {
            console.error('Storage event parsing error:', error)
          }
        } else {
          setTrackingItems([])
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const isPending = (conversationId: string) => {
    return trackingItems.some((item) => item.conversationId === conversationId)
  }

  return (
    <TrendV2Context.Provider value={{ trackingItems, isPending }}>
      {trackingItems.map((item) => (
        <TrendV2Monitor key={item.messageId} item={item} />
      ))}
      {children}
    </TrendV2Context.Provider>
  )
}

export const useTrendV2Tracking = () => {
  const context = useContext(TrendV2Context)
  if (context === undefined) {
    throw new Error('useTrendV2Tracking must be used within TrendV2Provider')
  }
  return context
}
